import { useState, useEffect } from 'react';
import axios from 'axios';

const HOSTAPI = process.env.REACT_APP_BASE_URL;
const PUBLIC_URL = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');

const resolvePublicAssetUrl = (value?: string) => {
    if (!value) return value;
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
        return value;
    }
    const normalized = value.startsWith('/') ? value : `/${value}`;
    return `${PUBLIC_URL}${normalized}` || normalized;
};

export interface BrandConfig {
    brand_name: string;
    domain_name: string;
    faviconUrl?: string;
    logoUrl?: string;
    title?: string;
}

// Global cache to prevent redundant API fetches across components on the same page load
let globalBrandsCache: BrandConfig[] | null = null;
let globalBrandsPromise: Promise<BrandConfig[]> | null = null;

export const useBrandConfig = (eventBrand?: string) => {
    const [brandConfig, setBrandConfig] = useState<BrandConfig | null>(null);

    useEffect(() => {
        let isMounted = true;

        const getBrands = async () => {
            if (globalBrandsCache) return globalBrandsCache;
            if (globalBrandsPromise) return globalBrandsPromise;
            
            globalBrandsPromise = axios.get(`${HOSTAPI}/events/wmp/brands`).then(res => res.data.data);
            try {
                globalBrandsCache = await globalBrandsPromise;
                return globalBrandsCache;
            } catch (err) {
                globalBrandsPromise = null;
                throw err;
            }
        };

        const loadBrand = async () => {
            try {
                const brands = await getBrands();
                if (!isMounted || !brands || !Array.isArray(brands)) return;

                const currentHostname = window.location.hostname;
                
                // Match rules, in order of priority:
                // 1. Match currentHostname exactly
                let matchedBrand = brands.find(b => {
                    try {
                        const brandUrl = new URL(b.domain_name);
                        return brandUrl.hostname === currentHostname;
                    } catch (e) {
                        return false;
                    }
                });

                // 2. Fallback to eventBrand match if hostname did not match
                if (!matchedBrand && eventBrand) {
                    matchedBrand = brands.find(b => b.domain_name === eventBrand);
                }

                if (matchedBrand) {
                    setBrandConfig({
                        ...matchedBrand,
                        faviconUrl: resolvePublicAssetUrl(matchedBrand.faviconUrl),
                        logoUrl: resolvePublicAssetUrl(matchedBrand.logoUrl),
                    });
                }
            } catch (error) {
                console.error('Failed to fetch brand config:', error);
            }
        };

        loadBrand();

        return () => {
            isMounted = false;
        };
    }, [eventBrand]);

    return brandConfig;
};

/**
 * Hook that automatically updates document.title and favicon
 * based on the matched brand. Drop into App.tsx as a one-liner.
 */
export const useBrandMeta = () => {
    const brandConfig = useBrandConfig();

    useEffect(() => {
        if (!brandConfig) return;

        if (brandConfig.title) {
            document.title = brandConfig.title;
        }

        if (brandConfig.faviconUrl) {
            const linkElements = document.querySelectorAll('link[rel*="icon"]');
            linkElements.forEach(link => {
                (link as HTMLLinkElement).href = brandConfig.faviconUrl!;
                if (brandConfig.faviconUrl?.endsWith('.svg')) {
                    (link as HTMLLinkElement).type = 'image/svg+xml';
                }
            });

            if (linkElements.length === 0) {
                const newLink = document.createElement('link');
                newLink.rel = 'icon';
                newLink.href = brandConfig.faviconUrl;
                if (brandConfig.faviconUrl?.endsWith('.svg')) {
                    newLink.type = 'image/svg+xml';
                }
                document.head.appendChild(newLink);
            }
        }
    }, [brandConfig]);
};
